import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ErrorBoundary } from "react-error-boundary";
import { Toaster } from "sonner";
import App from "./App";
import { SocketProvider } from "./lib/socket";
import { UnsavedChangesProvider } from "./contexts/UnsavedChangesContext";
import { GlobalUnsavedChangesModal } from "./components/ui/GlobalUnsavedChangesModal";
import "./index.css";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <h2 className="text-2xl font-medium text-red-800 mb-4">
          Something went wrong
        </h2>
        <pre className="text-sm text-gray-600 bg-gray-50 p-4 rounded overflow-auto">
          {error.message}
        </pre>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 w-full bg-brand text-white py-2 px-4 rounded hover:bg-brand-hover"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <UnsavedChangesProvider>
            <SocketProvider>
              <App />
              <GlobalUnsavedChangesModal />
              <Toaster
                position="top-right"
                richColors
                visibleToasts={1}
                toastOptions={{ className: "max-w-sm min-w-[280px]" }}
              />
            </SocketProvider>
          </UnsavedChangesProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
